import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AUD = { public_self_book: "Public", approved_recruiters: "Authorized Recruiters Only", internal_staff: "Internal Only", hidden_group: "Hidden" };
const EMPTY = { host_user_id: "", organization_id: "", mode: "user", booker_user_id: "", booker_role: "", appointment_type_ids: [], can_cancel: true, can_manage_all: false, active: true };

// Booking Access: permission to BOOK WITH a Host (never to act as the Host). Org-scoped for Group Admins.
export default function BookingAccessAdmin() {
  const [items, setItems] = useState(null);
  const [opts, setOpts] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/booking-access").then((r) => setItems(r.data.items)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  useEffect(() => { load(); api.get("/booking-access/options").then((r) => setOpts(r.data)).catch(() => setOpts(null)); }, []);

  const toggle = async (r) => {
    try { await api.patch(`/booking-access/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="booking-access-page">
      <PageHeader title="Booking Access" description="Control who may book meetings with which hosts, and which appointment types they may use."
        actions={<Button className="bg-brand hover:bg-brand-strong" disabled={!opts} onClick={() => setEdit({ ...EMPTY })} data-testid="ba-add"><Plus className="w-4 h-4 mr-1" />Add Booking Access</Button>} />
      <p className="text-xs text-slate-500 mb-4 max-w-3xl">Booking Access lets a recruiter schedule a Person with a host. It never shares the host's calendar, Microsoft account or CRM data. If no appointment types are selected, the booker sees nothing.</p>
      {items === null ? <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        : items.length === 0 ? <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slate-500" data-testid="ba-empty"><ShieldCheck className="w-8 h-8 mx-auto text-slate-300 mb-2" />No Booking Access records yet.</div>
        : (
          <div className="rounded-xl border border-slate-200 bg-white divide-y" data-testid="ba-list">
            {items.map((r) => (
              <div key={r.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`ba-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-slate-800"><span className="text-slate-400 font-normal">Booker:</span> {r.booker_label} <span className="text-slate-300">→</span> <span className="text-slate-400 font-normal">Host:</span> {r.host_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{r.organization_name} · {r.appointment_type_names.length ? r.appointment_type_names.join(", ") : "No appointment types (shows nothing)"}</p>
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    <Badge variant="outline" className={r.active ? "text-emerald-700 border-emerald-200" : "text-slate-400"}>{r.active ? "Active" : "Disabled"}</Badge>
                    <Badge variant="outline">{r.can_cancel ? "Can cancel" : "No cancel"}</Badge>
                    {r.can_manage_all && <Badge variant="outline" className="text-amber-700 border-amber-200">Manages all host meetings</Badge>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={!!r.active} onCheckedChange={() => toggle(r)} data-testid={`ba-active-${r.id}`} />
                  <Button size="sm" variant="outline" onClick={() => setEdit({ ...EMPTY, ...r, mode: r.booker_user_id ? "user" : "role" })} data-testid={`ba-edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      {edit && opts && <AccessDialog value={edit} opts={opts} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function AccessDialog({ value, opts, onClose, onSaved }) {
  const [f, setF] = useState(value);
  const [busy, setBusy] = useState(false);
  const host = opts.hosts.find((h) => h.user_id === f.host_user_id);
  const hostOrgs = opts.organizations.filter((o) => (host?.organization_ids || []).includes(o.id));
  const types = (host?.appointment_types || []).filter((t) => t.active !== false && t.audience !== "hidden_group");
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const toggleType = (id) => set({ appointment_type_ids: f.appointment_type_ids.includes(id) ? f.appointment_type_ids.filter((x) => x !== id) : [...f.appointment_type_ids, id] });

  const save = async () => {
    setBusy(true);
    const body = { host_user_id: f.host_user_id, organization_id: f.organization_id, appointment_type_ids: f.appointment_type_ids,
      can_cancel: f.can_cancel, can_manage_all: f.can_manage_all, active: f.active,
      booker_user_id: f.mode === "user" ? f.booker_user_id : null, booker_role: f.mode === "role" ? f.booker_role : null };
    try {
      if (f.id) await api.patch(`/booking-access/${f.id}`, body); else await api.post("/booking-access", body);
      toast.success("Booking Access saved"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="ba-dialog">
        <DialogHeader><DialogTitle>{f.id ? "Edit Booking Access" : "Add Booking Access"}</DialogTitle>
          <DialogDescription>Who may book with this host, and which appointment types.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Pick label="Host" value={f.host_user_id} onChange={(v) => set({ host_user_id: v, organization_id: "", appointment_type_ids: [] })} tid="ba-host"
            options={opts.hosts.map((h) => [h.user_id, h.name])} />
          <Pick label="Organization" value={f.organization_id} onChange={(v) => set({ organization_id: v })} tid="ba-org" options={hostOrgs.map((o) => [o.id, o.name])} />
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1 block">Who may book</Label>
            <div className="flex gap-2 mb-2">
              {[["user", "Specific user"], ["role", "Role in this organization"]].map(([k, l]) => (
                <button key={k} type="button" onClick={() => set({ mode: k })} data-testid={`ba-mode-${k}`}
                  className={`text-xs rounded-lg border px-3 py-1.5 ${f.mode === k ? "border-brand text-brand bg-brand/5" : "border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
            </div>
            {f.mode === "user"
              ? <Pick value={f.booker_user_id} onChange={(v) => set({ booker_user_id: v })} tid="ba-booker-user"
                  options={opts.bookers.filter((b) => b.id !== f.host_user_id).map((b) => [b.id, `${b.name} · ${b.email}`])} />
              : <Pick value={f.booker_role} onChange={(v) => set({ booker_role: v })} tid="ba-booker-role" options={opts.roles.map((r) => [r.value, r.label])} />}
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1 block">Allowed appointment types</Label>
            {!host ? <p className="text-xs text-slate-400">Select a host first.</p> : types.length === 0 ? <p className="text-xs text-slate-400">This host has no active appointment types.</p> : (
              <div className="space-y-1.5" data-testid="ba-types">
                {types.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 cursor-pointer">
                    <Checkbox checked={f.appointment_type_ids.includes(t.id)} onCheckedChange={() => toggleType(t.id)} data-testid={`ba-type-${t.id}`} />
                    <span className="text-sm text-slate-700 flex-1">{t.name}</span>
                    <span className="text-[11px] text-slate-400">{AUD[t.audience] || t.audience}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <Toggle label="Can Cancel Meetings" desc="Booker may cancel meetings they booked." checked={f.can_cancel} onChange={(v) => set({ can_cancel: v })} tid="ba-can-cancel" />
          <Toggle label="Can Manage Delegated Host Meetings" desc="Manage ALL of this host's applicable meetings, not only ones they booked. For internal staff." checked={f.can_manage_all} onChange={(v) => set({ can_manage_all: v })} tid="ba-can-manage-all" />
          <Toggle label="Active" checked={f.active} onChange={(v) => set({ active: v })} tid="ba-active" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-brand hover:bg-brand-strong" disabled={busy || !f.host_user_id || !f.organization_id || !(f.mode === "user" ? f.booker_user_id : f.booker_role)} onClick={save} data-testid="ba-save">{busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Pick({ label, value, onChange, options, tid }) {
  return (
    <div>
      {label && <Label className="text-xs font-semibold text-slate-600 mb-1 block">{label}</Label>}
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={tid}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent className="bg-white">{options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}

function Toggle({ label, desc, checked, onChange, tid }) {
  return (
    <label className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2" data-testid={tid}>
      <span className="text-sm text-slate-700">{label}{desc && <span className="block text-[11px] text-slate-400">{desc}</span>}</span>
      <Switch checked={!!checked} onCheckedChange={onChange} />
    </label>
  );
}
